<template>
  <div class="bg-white p-4 rounded-lg mt-4 text-left text-textPrimary">
    <h2 class="text-md font-bold">Jornada excepcional</h2>

    <div class="flex flex-col gap-4 mt-4">
      <div class="text-left">
        <h3 class="text-md font-semibold mb-2">Início</h3>
        <div class="bg-white flex flex-row gap-2">
          <div
            class="flex flex-row items-center rounded border border-secondary50 gap-2 py-2 px-2 flex-1"
          >
            <input
              type="date"
              v-model="startDate"
              class="bg-transparent focus:outline-none w-full text-sm"
              :min="startDate"
              :max="endDate"
            />
          </div>
          <div
            class="flex flex-row items-center rounded border border-secondary50 gap-2 py-2 px-2 flex-1"
          >
            <input
              type="time"
              v-model="startTime"
              class="bg-transparent focus:outline-none w-full text-sm"
            />
          </div>
        </div>
      </div>

      <div class="text-left">
        <h3 class="text-md font-semibold mb-2">Fim</h3>
        <div class="bg-white flex flex-row gap-2">
          <div
            class="flex flex-row items-center rounded border border-secondary50 gap-2 py-2 px-2 flex-1"
          >
            <input
              type="date"
              v-model="endDate"
              class="bg-transparent focus:outline-none w-full text-sm"
              :min="startDate"
              :max="endDate"
            />
          </div>
          <div
            class="flex flex-row items-center rounded border border-secondary50 gap-2 py-2 px-2 flex-1"
          >
            <input
              type="time"
              v-model="endTime"
              class="bg-transparent focus:outline-none w-full text-sm"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4 mt-6">
      <div class="text-left">
        <h3 class="text-lg font-bold">Horas perdidas</h3>
        <p class="mt-1 text-md text-negative">{{ lostHoursFormatted }}</p>
      </div>
      <!-- <div class="text-left">
        <h3 class="text-lg font-bold">Prejuízo previsto</h3>
        <p class="mt-1 text-md text-negative">{{ predictedLossFormatted }}</p>
      </div> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { differenceInMinutes, format, formatDuration } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const props = defineProps<{
  modelValue: { start: string; end: string }
  originalStart: string
  originalEnd: string
  baseHourlyRate: number
  timezone: string
}>()

const emit = defineEmits(['update:modelValue'])

const splitDateTime = (isoString: string, timezone: string | undefined) => {
  if (!isoString) return { date: '', time: '' }
  const targetTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  try {
    const date = new Date(isoString)
    if (isNaN(date.getTime())) return { date: '', time: '' }

    const datePart = new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: targetTimezone
    }).format(date)

    const timePart = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: targetTimezone
    }).format(date)

    return { date: datePart, time: timePart }
  } catch (e) {
    const fallbackDate = new Date(isoString)
    return {
      date: format(fallbackDate, 'yyyy-MM-dd'),
      time: format(fallbackDate, 'HH:mm')
    }
  }
}

const startDate = ref(splitDateTime(props.originalStart, props.timezone).date)
const startTime = ref(splitDateTime(props.originalStart, props.timezone).time)
const endDate = ref(splitDateTime(props.originalEnd, props.timezone).date)
const endTime = ref(splitDateTime(props.originalEnd, props.timezone).time)

const getDurationInMinutes = (startStr: string, endStr: string): number => {
  if (!startStr || !endStr) return 0
  const start = new Date(startStr)
  const end = new Date(endStr)

  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 0
  return differenceInMinutes(end, start)
}

const baseDurationInMinutes = computed(() =>
  getDurationInMinutes(props.originalStart, props.originalEnd)
)

const exceptionalDurationInMinutes = computed(() => {
  if (startDate.value && endDate.value) {
    const start = `${startDate.value}T${startTime.value || '00:00'}`
    const end = `${endDate.value}T${endTime.value || '00:00'}`
    return getDurationInMinutes(start, end)
  }
  return 0
})

const lostMinutes = computed(() => baseDurationInMinutes.value - exceptionalDurationInMinutes.value)

const lostHoursFormatted = computed(() => {
  if (lostMinutes.value <= 0) return '0 horas e 0 minutos'
  return formatDuration(
    {
      hours: Math.floor(lostMinutes.value / 60),
      minutes: lostMinutes.value % 60
    },
    { format: ['hours', 'minutes'], locale: ptBR }
  )
})

// const predictedLossFormatted = computed(() => {
//   if (lostMinutes.value <= 0)
//     return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(0)

//   const lostHours = lostMinutes.value / 60
//   const lossValue = lostHours * props.baseHourlyRate

//   return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lossValue)
// })

watch([startDate, startTime, endDate, endTime], () => {
  if (startDate.value && endDate.value) {
    const fullStartDateTime = `${startDate.value}T${startTime.value || '00:00'}`
    const fullEndDateTime = `${endDate.value}T${endTime.value || '00:00'}`
    emit('update:modelValue', {
      start: fullStartDateTime,
      end: fullEndDateTime
    })
  }
})
</script>
